import React, { useState, useEffect } from 'react';
import appScriptService from '../utils/appScriptService';
import quizGeneratorService from '../utils/quizGeneratorService';

const QuizGenerate = () => {
  const [selectedModule, setSelectedModule] = useState('LM2001020230_프로그래밍언어응용');
  const [summaryData, setSummaryData] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // NCS 모듈 목록
  const modules = [
    { id: 'LM2001020230_프로그래밍언어응용', name: '프로그래밍 언어 응용' },
    { id: 'LM2001020231_프로그래밍언어활용', name: '프로그래밍 언어 활용' },
    { id: 'LM2001020224_화면설계', name: '화면설계' },
    { id: 'LM2001020225_화면구현', name: '화면구현' },
    { id: 'LM2001020211_서버프로그램구현', name: '서버프로그램구현' },
    { id: 'LM2001020227_애플리케이션테스트수행', name: '애플리케이션테스트수행' },
    { id: 'LM2001020205_데이터입출력구현', name: '데이터입출력구현' },
    { id: 'LM2001020206_통합구현', name: '통합구현' },
    { id: 'LM2001020214_애플리케이션배포', name: '애플리케이션배포' },
    { id: 'LM2001020212_인터페이스구현', name: '인터페이스구현' },
  ];

  useEffect(() => {
    loadSummaryData();
  }, [selectedModule]);

  // 학습 요약 데이터 로드
  const loadSummaryData = async () => {
    try {
      const data = await appScriptService.getData('AI요약', {
        과목코드: selectedModule
      });
      
      const summariesData = Array.isArray(data) ? data : (data?.data || []);
      setSummaryData(summariesData);
      setSelectedTopics([]); // 모듈 변경 시 선택 토픽 초기화
      setGeneratedQuestions([]); // 기존 생성 문제 초기화
      
    } catch (error) {
      console.error('학습 데이터 로드 실패:', error);
      setSummaryData([]);
    }
  };

  // 토픽 선택 토글
  const toggleTopicSelection = (topicId) => {
    setSelectedTopics(prev => 
      prev.includes(topicId) 
        ? prev.filter(id => id !== topicId)
        : [...prev, topicId]
    );
  };

  // 전체 선택/해제
  const toggleAllTopics = () => {
    if (selectedTopics.length === summaryData.length) {
      setSelectedTopics([]);
    } else {
      setSelectedTopics(summaryData.map((item, index) => item.토픽ID || item['토픽ID'] || `topic-${index}`));
    }
  };

  // AI 문제 생성
  const generateQuestions = async () => {
    if (selectedTopics.length === 0) {
      alert('문제를 생성할 토픽을 선택해주세요.');
      return;
    }

    setIsGenerating(true);
    setGenerationProgress('문제 생성을 준비하는 중...');
    
    try {
      // 선택된 토픽의 요약 데이터만 필터링
      const selectedSummaries = summaryData.filter((item, index) => {
        const topicId = item.토픽ID || item['토픽ID'] || `topic-${index}`;
        return selectedTopics.includes(topicId);
      });

      const result = await quizGeneratorService.generateBatchQuestions(
        selectedSummaries,
        (progress) => setGenerationProgress(progress)
      );

      if (result.success) {
        setGeneratedQuestions(result.questions);
        setGenerationProgress(`✅ 총 ${result.totalGenerated}개 문제 생성 완료!`);
        setTimeout(() => setGenerationProgress(''), 3000);
      } else {
        throw new Error('문제 생성에 실패했습니다.');
      }
      
    } catch (error) {
      console.error('문제 생성 실패:', error);
      alert('문제 생성에 실패했습니다. 다시 시도해주세요.');
      setGenerationProgress('');
    } finally {
      setIsGenerating(false);
    }
  };

  // 문제 편집
  const startEditQuestion = (question, index) => {
    setEditingQuestion({
      ...question,
      index: index
    });
  };

  // 편집 저장
  const saveEditedQuestion = () => {
    const updatedQuestions = [...generatedQuestions];
    const { index, ...questionData } = editingQuestion;
    updatedQuestions[index] = {
      ...questionData,
      수정일시: new Date().toISOString()
    };
    setGeneratedQuestions(updatedQuestions);
    setEditingQuestion(null);
  };

  // 문제 삭제
  const deleteQuestion = (index) => {
    if (window.confirm('이 문제를 삭제하시겠습니까?')) {
      const updatedQuestions = generatedQuestions.filter((_, i) => i !== index);
      setGeneratedQuestions(updatedQuestions);
    }
  };

  // Google Sheets에 저장
  const saveQuestionsToSheet = async () => {
    if (generatedQuestions.length === 0) {
      alert('저장할 문제가 없습니다.');
      return;
    }

    if (!window.confirm(`${generatedQuestions.length}개 문제를 Google Sheets에 저장하시겠습니까?`)) {
      return;
    }

    setIsSaving(true);
    
    try {
      // Google Sheets 형식으로 변환
      const sheetData = generatedQuestions.map((q, index) => ({
        id: `${selectedModule}_${Date.now()}_${index}`,
        과목코드: q.과목코드,
        과목명: q.과목명,
        토픽ID: q.sourceTopicId,
        토픽명: q.sourceTopicName,
        문제유형: '객관식',
        난이도: q.difficulty,
        문제: q.question,
        선택지1: q.options[0],
        선택지2: q.options[1],
        선택지3: q.options[2],
        선택지4: q.options[3],
        정답: q.correctAnswer,
        해설: q.explanation,
        키워드: Array.isArray(q.keywords) ? q.keywords.join(', ') : q.keywords,
        출제빈도: 0,
        정답률: null,
        생성일시: q.생성일시,
        수정일시: q.수정일시 || q.생성일시
      }));

      await appScriptService.addData('객관식문제', sheetData);
      
      alert('문제가 성공적으로 저장되었습니다!');
      setGeneratedQuestions([]); // 저장 후 초기화
      
    } catch (error) {
      console.error('문제 저장 실패:', error);
      alert('문제 저장에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsSaving(false);
    }
  };

  const getSelectedModuleInfo = () => {
    return modules.find(module => module.id === selectedModule) || modules[0];
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case '상': return 'bg-red-100 text-red-700';
      case '중': return 'bg-yellow-100 text-yellow-700';
      case '하': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8">
      <div className="max-w-7xl mx-auto px-4 overflow-x-hidden">
        {/* 헤더 */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
            🎓 AI 객관식 문제 생성기
          </h1>
          <p className="text-gray-600 text-lg">
            교사용 - AI를 활용한 고품질 객관식 문제 제작 도구
          </p>
        </div>

        {/* 설정 패널 */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            ⚙️ 문제 생성 설정
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 모듈 선택 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                학습 모듈 선택
              </label>
              <select
                value={selectedModule}
                onChange={(e) => setSelectedModule(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isGenerating}
              >
                {modules.map(module => (
                  <option key={module.id} value={module.id}>
                    {module.name}
                  </option>
                ))}
              </select>
              <p className="text-sm text-gray-500 mt-2">
                📊 총 {summaryData.length}개 토픽 로드됨
              </p>
            </div>

            {/* 생성 상태 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                문제 생성 현황
              </label>
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {generatedQuestions.length} 문제
                </div>
                <div className="text-sm text-blue-700">
                  선택된 토픽: {selectedTopics.length}개 / {summaryData.length}개
                </div>
                {generationProgress && (
                  <div className="text-sm text-blue-600 mt-2 font-medium">
                    {generationProgress}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 토픽 선택 */}
        {summaryData.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">📋 문제 생성할 토픽 선택</h3>
              <div className="flex gap-2">
                <button
                  onClick={toggleAllTopics}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
                  disabled={isGenerating}
                >
                  {selectedTopics.length === summaryData.length ? '전체 해제' : '전체 선택'}
                </button>
                <button
                  onClick={generateQuestions}
                  disabled={selectedTopics.length === 0 || isGenerating}
                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isGenerating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      문제 생성 중...
                    </>
                  ) : (
                    '🤖 AI 문제 생성'
                  )}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
              {summaryData.map((item, index) => {
                // 실제 데이터 구조에 맞게 토픽ID 설정
                const topicId = item.토픽ID || item['토픽ID'] || `topic-${index}`;
                const isSelected = selectedTopics.includes(topicId);
                
                // 토픽명과 핵심개념 추출
                const topicName = item.토픽명 || item['토픽명'] || item.학습제목 || `토픽 ${topicId}`;
                const coreContent = item.핵심개념 || item['핵심개념'] || '';
                
                
                return (
                  <label
                    key={`${topicId}-${index}`}
                    className={`flex items-start p-3 rounded-lg border-2 cursor-pointer transition-all min-w-0 ${
                      isSelected 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleTopicSelection(topicId)}
                      className="mr-3 h-4 w-4 text-blue-600 rounded focus:ring-blue-500 flex-shrink-0 mt-0.5"
                      disabled={isGenerating}
                    />
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="font-medium text-sm text-gray-900 break-words leading-tight">
                        {topicName}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 line-clamp-2 break-words">
                        {coreContent}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* 생성된 문제 목록 */}
        {generatedQuestions.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-semibold">📝 생성된 문제 목록</h3>
              <button
                onClick={saveQuestionsToSheet}
                disabled={isSaving}
                className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    저장 중...
                  </>
                ) : (
                  '💾 Google Sheets에 저장'
                )}
              </button>
            </div>

            <div className="space-y-6">
              {generatedQuestions.map((question, index) => {
                const validation = quizGeneratorService.validateQuestion(question);
                
                return (
                  <div key={index} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                          문제 {index + 1}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getDifficultyColor(question.difficulty)}`}>
                          {question.difficulty}
                        </span>
                        <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">
                          {question.type}
                        </span>
                        {!validation.isValid && (
                          <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs">
                            ⚠️ 품질 이슈
                          </span>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <button
                          onClick={() => startEditQuestion(question, index)}
                          className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                        >
                          ✏️ 편집
                        </button>
                        <button
                          onClick={() => deleteQuestion(index)}
                          className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                        >
                          🗑️ 삭제
                        </button>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="text-sm text-gray-600 mb-2 truncate">
                        토픽: {question.sourceTopicName}
                      </div>
                      <div className="font-medium text-lg text-gray-900 mb-4 break-words">
                        {question.question}
                      </div>
                      
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-2 mb-4">
                        {question.options.map((option, optIndex) => (
                          <div
                            key={optIndex}
                            className={`p-3 rounded-lg border min-w-0 ${
                              question.correctAnswer === optIndex + 1
                                ? 'border-green-500 bg-green-50'
                                : 'border-gray-200 bg-gray-50'
                            }`}
                          >
                            <div className="flex items-start gap-2">
                              <span className="font-medium text-sm text-gray-600 flex-shrink-0">
                                {optIndex + 1}. 
                              </span>
                              <span className={`flex-1 break-words ${
                                question.correctAnswer === optIndex + 1 
                                  ? 'text-green-800 font-medium' 
                                  : 'text-gray-700'
                              }`}>
                                {option}
                              </span>
                              {question.correctAnswer === optIndex + 1 && (
                                <span className="text-green-600 flex-shrink-0">✓</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <div className="text-sm font-medium text-blue-800 mb-1">💡 해설</div>
                        <div className="text-blue-700 text-sm break-words">{question.explanation}</div>
                      </div>
                      
                      {question.keywords && question.keywords.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-3">
                          {question.keywords.map((keyword, kwIndex) => (
                            <span key={kwIndex} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs break-all">
                              {keyword}
                            </span>
                          ))}
                        </div>
                      )}

                      {!validation.isValid && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <div className="text-sm font-medium text-red-800 mb-1">⚠️ 품질 개선 필요</div>
                          <ul className="text-sm text-red-700 list-disc list-inside">
                            {validation.issues.map((issue, issueIndex) => (
                              <li key={issueIndex}>{issue}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 문제 편집 모달 */}
        {editingQuestion && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h3 className="text-lg font-bold mb-4">문제 편집</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">문제</label>
                    <textarea
                      value={editingQuestion.question}
                      onChange={(e) => setEditingQuestion({...editingQuestion, question: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {editingQuestion.options.map((option, index) => (
                      <div key={index}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          선택지 {index + 1} {editingQuestion.correctAnswer === index + 1 && '(정답)'}
                        </label>
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => {
                            const newOptions = [...editingQuestion.options];
                            newOptions[index] = e.target.value;
                            setEditingQuestion({...editingQuestion, options: newOptions});
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    ))}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">정답 번호</label>
                      <select
                        value={editingQuestion.correctAnswer}
                        onChange={(e) => setEditingQuestion({...editingQuestion, correctAnswer: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      >
                        {[1,2,3,4].map(num => (
                          <option key={num} value={num}>{num}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">난이도</label>
                      <select
                        value={editingQuestion.difficulty}
                        onChange={(e) => setEditingQuestion({...editingQuestion, difficulty: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="하">하</option>
                        <option value="중">중</option>
                        <option value="상">상</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">해설</label>
                    <textarea
                      value={editingQuestion.explanation}
                      onChange={(e) => setEditingQuestion({...editingQuestion, explanation: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setEditingQuestion(null)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
                  >
                    취소
                  </button>
                  <button
                    onClick={saveEditedQuestion}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    저장
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizGenerate;